import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { 
  Book,
  BookOpen,
  Mail,
  ExternalLink,
  Star,
  Check,
  Send,
  Menu,
  X,
  Calendar,
  ArrowRight,
  ShoppingCart,
  FileText
} from 'lucide-react';
import { landingPageData } from '../mockData';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { useNavigate } from 'react-router-dom';

const AuthorLandingPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    console.log('Form submitted:', formData);
    toast.success('Thank you for your message! Dr. Miller will respond soon.');
    setFormData({
      name: '',
      email: '',
      subject: '',
      message: ''
    });
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-lg border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <Book className="w-6 h-6 text-amber-600" />
              <span className="text-xl font-bold text-gray-900">
                Carolyn Godschild Miller
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('books')} className="text-gray-700 hover:text-amber-600 transition-colors font-medium">
                Books
              </button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-amber-600 transition-colors font-medium">
                About
              </button>
              <button onClick={() => scrollToSection('blog')} className="text-gray-700 hover:text-amber-600 transition-colors font-medium">
                Blog
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 hover:text-amber-600 transition-colors font-medium">
                FAQ
              </button>
              <Button onClick={() => scrollToSection('contact')} className="bg-amber-600 hover:bg-amber-700 text-white">
                Contact
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('books')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 rounded-lg transition-colors">
                Books
              </button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 rounded-lg transition-colors">
                About
              </button>
              <button onClick={() => scrollToSection('blog')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 rounded-lg transition-colors">
                Blog
              </button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-amber-50 rounded-lg transition-colors">
                FAQ
              </button>
              <Button onClick={() => scrollToSection('contact')} className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                Contact
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 bg-gradient-to-br from-amber-50 via-white to-orange-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Text Content */}
            <div className="order-2 lg:order-1">
              <Badge className="mb-4 bg-amber-100 text-amber-800 hover:bg-amber-200">
                {landingPageData.hero.subtitle}
              </Badge>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                {landingPageData.hero.title}
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                {landingPageData.hero.description}
              </p>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-12">
                <Button 
                  size="lg" 
                  onClick={() => scrollToSection('books')}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                >
                  {landingPageData.hero.ctaText}
                  <BookOpen className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => window.open(landingPageData.contact.blog, '_blank')}
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 text-lg px-8 py-6 rounded-lg transition-all"
                >
                  {landingPageData.hero.ctaSecondary}
                  <ExternalLink className="ml-2 w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {landingPageData.about.highlights.map((highlight, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold text-amber-600 mb-1">
                      {highlight.number}
                    </div>
                    <div className="text-sm text-gray-600">
                      {highlight.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Author Photo */}
            <div className="order-1 lg:order-2">
              <div className="relative max-w-md mx-auto">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-400 rounded-3xl transform rotate-2 opacity-10"></div>
                <div className="relative bg-white rounded-3xl shadow-2xl p-2 border-4 border-white">
                  <img 
                    src={landingPageData.hero.authorPhoto}
                    alt="Dr. Carolyn Godschild Miller"
                    className="w-full h-auto rounded-2xl"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-amber-600 text-white px-6 py-4 rounded-2xl shadow-xl">
                  <div className="flex items-center space-x-2">
                    <Book className="w-6 h-6" />
                    <span className="font-bold">5 Published Books</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Books Section */}
      <section id="books" className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-amber-100 text-amber-800 hover:bg-amber-200">
                Published Works
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                Books by Dr. Miller
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Explore miracles, soulmates, and spiritual awakening through psychology and divine guidance
              </p>
            </div>

            {/* Featured Book Highlight */}
            <div className="mb-16 bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 rounded-3xl p-8 md:p-12 border-2 border-amber-200 shadow-xl">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                </div>
                <div>
                  <Badge className="mb-3 bg-amber-600 text-white hover:bg-amber-700">
                    Forthcoming Book
                  </Badge>
                  <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                    {landingPageData.featuredBook.title}
                  </h3>
                  <p className="text-xl text-amber-700 font-semibold mb-6">
                    {landingPageData.featuredBook.subtitle}
                  </p>
                </div>
              </div>
              
              <div className="prose prose-lg max-w-none">
                {landingPageData.featuredBook.description.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}
              </div>
              
              <div className="mt-8 flex justify-center">
                <Button 
                  size="lg"
                  onClick={() => navigate('/self-healing-appendices')}
                  className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6"
                >
                  <FileText className="mr-2 w-5 h-5" />
                  Read Appendices & Supplementary Material
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Appendices Section - Moved Here */}
            <div className="mb-16 bg-gradient-to-br from-indigo-50 via-violet-50 to-purple-50 rounded-3xl p-8 md:p-12 border-2 border-indigo-200 shadow-xl">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-600 rounded-full mb-4">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <Badge className="mb-4 bg-indigo-600 text-white hover:bg-indigo-700">
                  Supplementary Materials
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  {landingPageData.appendicesInfo.title}
                </h2>
                <p className="text-xl text-indigo-700 font-semibold mb-4">
                  {landingPageData.appendicesInfo.subtitle}
                </p>
                <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
                  {landingPageData.appendicesInfo.description}
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-md border border-indigo-100">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <span className="text-indigo-600 font-bold">I</span>
                    </div>
                    <h3 className="font-bold text-gray-900">Professional Background</h3>
                  </div>
                  <p className="text-sm text-gray-600">Ph.D., NIMH Fellowship, Sigma Xi membership, clinical practice</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-indigo-100">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <span className="text-indigo-600 font-bold">II</span>
                    </div>
                    <h3 className="font-bold text-gray-900">My Relationship with Jesus</h3>
                  </div>
                  <p className="text-sm text-gray-600">The powerful story of spiritual guidance and transformation</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-indigo-100">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <span className="text-indigo-600 font-bold">III</span>
                    </div>
                    <h3 className="font-bold text-gray-900">Previous Publications</h3>
                  </div>
                  <p className="text-sm text-gray-600">Complete bibliography of all published works</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-indigo-100">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <span className="text-indigo-600 font-bold">IV</span>
                    </div>
                    <h3 className="font-bold text-gray-900">A Course in Miracles</h3>
                  </div>
                  <p className="text-sm text-gray-600">Complete history, origins, and Helen Schucman's story</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-md border border-indigo-100 md:col-span-2">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <span className="text-indigo-600 font-bold">V</span>
                    </div>
                    <h3 className="font-bold text-gray-900">Hindu Chakra System Guide</h3>
                  </div>
                  <p className="text-sm text-gray-600">Complete guide to all seven chakras and spiritual evolution</p>
                </div>
              </div>

              <div className="text-center">
                <Button 
                  size="lg"
                  onClick={() => navigate('/appendices')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white text-lg px-8 py-6 shadow-lg"
                >
                  <FileText className="mr-2 w-5 h-5" />
                  {landingPageData.appendicesInfo.ctaText}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="space-y-16">
              {landingPageData.books.map((book, index) => (
                <Card key={book.id} className={`overflow-hidden border-2 border-gray-100 hover:border-amber-200 transition-all hover:shadow-2xl ${index % 2 === 0 ? '' : 'bg-gradient-to-br from-amber-50/50 to-orange-50/50'}`}>
                  <div className={`grid md:grid-cols-${index % 2 === 0 ? '2' : '2'} gap-8 p-8`}>
                    {/* Book Cover */}
                    <div className={`${index % 2 === 0 ? 'order-1' : 'order-1 md:order-2'} flex justify-center items-center`}>
                      <div className="relative group">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-400 rounded-2xl transform rotate-2 opacity-20 group-hover:rotate-6 transition-transform"></div>
                        <img 
                          src={book.coverImage}
                          alt={book.title}
                          className="relative w-64 h-auto rounded-xl shadow-2xl transform group-hover:scale-105 transition-transform"
                        />
                      </div>
                    </div>

                    {/* Book Details */}
                    <div className={`${index % 2 === 0 ? 'order-2' : 'order-2 md:order-1'} flex flex-col justify-center`}>
                      <div className="mb-4">
                        <h3 className="text-3xl font-bold text-gray-900 mb-2">
                          {book.title}
                        </h3>
                        <p className="text-lg text-amber-600 font-medium mb-3">
                          {book.subtitle}
                        </p>
                        <p className="text-sm text-gray-500">
                          by {book.author}
                        </p>
                      </div>

                      {book.rating > 0 && (
                        <div className="flex items-center space-x-2 mb-4">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <Star 
                                key={i} 
                                className={`w-4 h-4 ${i < Math.floor(book.rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                              />
                            ))}
                          </div>
                          <span className="text-sm text-gray-600">
                            {book.rating} ({book.reviews} reviews)
                          </span>
                        </div>
                      )}

                      <p className="text-gray-700 mb-6 leading-relaxed">
                        {book.description}
                      </p>

                      <ul className="space-y-2 mb-6">
                        {book.highlights.map((highlight, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <Check className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-600">{highlight}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="flex flex-wrap items-center gap-3 mb-6">
                        <span className="text-2xl font-bold text-gray-900">{book.price}</span>
                        {book.formats.map((format, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {format}
                          </Badge>
                        ))}
                      </div>

                      <Button 
                        onClick={() => window.open(book.amazonLink, '_blank')}
                        className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6 w-full sm:w-auto"
                      >
                        <ShoppingCart className="mr-2 w-5 h-5" />
                        Buy on Amazon
                        <ExternalLink className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-gradient-to-br from-amber-50 via-orange-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-amber-100 text-amber-800 hover:bg-amber-200">
                {landingPageData.about.tagline}
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {landingPageData.about.title}
              </h2>
            </div>

            {/* Photo with Arnold */}
            <div className="mb-12 flex justify-center">
              <div className="relative max-w-md">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-400 rounded-3xl transform rotate-1 opacity-10"></div>
                <div className="relative bg-white rounded-3xl shadow-xl p-2 border-4 border-white">
                  <img 
                    src={landingPageData.hero.authorPhotoWithArnold}
                    alt="Carolyn Miller and Arnold Weiss"
                    className="w-full h-auto rounded-2xl"
                  />
                  <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur-sm rounded-xl px-4 py-3 shadow-lg border border-gray-200">
                    <p className="text-center text-gray-800 font-semibold">
                      Carolyn Miller and Arnold Weiss
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Opening Statement */}
            <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-3xl p-8 md:p-10 mb-8 border-l-4 border-amber-600">
              <p className="text-lg text-gray-800 leading-relaxed italic">
                {landingPageData.about.opening}
              </p>
            </div>

            {/* Autobiography Section */}
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-200 mb-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-1 h-8 bg-amber-600 mr-4"></div>
                {landingPageData.about.autobiography.title}
              </h3>
              <div className="prose prose-lg max-w-none">
                {landingPageData.about.autobiography.content.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-6">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Spiritual Education Section */}
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-200">
              <h3 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-1 h-8 bg-amber-600 mr-4"></div>
                {landingPageData.about.spiritualEducation.title}
              </h3>
              
              {/* Carmela Corallo Photo */}
              <div className="mb-8 flex justify-center">
                <div className="relative max-w-xs">
                  <div className="bg-white rounded-2xl shadow-lg p-4 border-2 border-amber-200">
                    <img 
                      src={landingPageData.hero.carmelaPhoto}
                      alt="Carmela Corallo, circa 1980"
                      className="w-full h-auto rounded-xl"
                    />
                    <p className="text-center text-sm text-gray-600 mt-3 italic">
                      Carmela Corallo, circa 1980<br/>
                      Enlightened Teacher of Siddha Yoga
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="prose prose-lg max-w-none">
                {landingPageData.about.spiritualEducation.content.split('\n\n').map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-6">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>

            {/* Blog CTA */}
            <div className="mt-8 text-center bg-amber-50 rounded-2xl p-6 border border-amber-200">
              <p className="text-gray-700 mb-4">
                To read more about A Course in Miracles, ask questions or make comments, visit Carolyn's blog
              </p>
              <Button 
                onClick={() => window.open(landingPageData.contact.blog, '_blank')}
                variant="outline"
                className="border-amber-600 text-amber-600 hover:bg-amber-50"
              >
                <BookOpen className="mr-2 w-5 h-5" />
                Visit godschild990.blogspot.com
                <ExternalLink className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-amber-100 text-amber-800 hover:bg-amber-200">
                Praise for the Books
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                What Readers Say
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {landingPageData.testimonials.map((testimonial) => (
                <Card key={testimonial.id} className="border-2 border-gray-100 hover:border-amber-200 transition-all hover:shadow-lg">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-4">
                      <Badge variant="outline" className="text-xs">
                        {testimonial.book}
                      </Badge>
                      <div className="flex items-center space-x-1">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                        ))}
                      </div>
                    </div>
                    <CardDescription className="text-base text-gray-700 leading-relaxed">
                      "{testimonial.text}"
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-sm text-gray-500">{testimonial.role}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section id="blog" className="py-24 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-amber-100 text-amber-800 hover:bg-amber-200">
                Latest Insights
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                From the Blog
              </h2>
              <p className="text-xl text-gray-600">
                Reflections on A Course in Miracles
              </p>
            </div>

            <div className="grid md:grid-cols-1 gap-8 mb-12">
              {landingPageData.blogPosts.map((post) => (
                <Card key={post.id} className="border-2 border-gray-100 hover:border-amber-200 transition-all hover:shadow-xl bg-white">
                  <CardHeader>
                    <div className="flex items-center space-x-2 text-sm text-gray-500 mb-3">
                      <Calendar className="w-4 h-4" />
                      <span>{post.date}</span>
                    </div>
                    <CardTitle className="text-2xl mb-3">{post.title}</CardTitle>
                    <CardDescription className="text-base leading-relaxed">
                      {post.excerpt}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      variant="outline"
                      onClick={() => window.open(post.link, '_blank')}
                      className="border-amber-600 text-amber-600 hover:bg-amber-50"
                    >
                      Read Full Article
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="text-center">
              <Button 
                size="lg"
                onClick={() => window.open(landingPageData.contact.blog, '_blank')}
                className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6"
              >
                Visit Full Blog
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-amber-100 text-amber-800 hover:bg-amber-200">
                FAQ
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                Frequently Asked Questions
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {landingPageData.faq.map((item, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white border-2 border-gray-100 rounded-xl px-6 hover:border-amber-200 transition-colors"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-amber-600 py-5">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed pb-5">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Share Your Miracle Section */}
      <section className="py-24 bg-gradient-to-br from-amber-50 via-orange-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-amber-600 text-white hover:bg-amber-700">
                Miracle Stories
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {landingPageData.shareYourMiracle.title}
              </h2>
              <p className="text-2xl text-amber-700 font-semibold mb-4">
                {landingPageData.shareYourMiracle.subtitle}
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                {landingPageData.shareYourMiracle.description}
              </p>
            </div>

            <Card className="border-2 border-amber-200 shadow-xl bg-white">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="miracle-name" className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <Input
                        id="miracle-name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Doe"
                        className="border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="miracle-email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="miracle-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="john@example.com"
                        className="border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="miracle-story" className="block text-sm font-medium text-gray-700 mb-2">
                      Your Miracle Story *
                    </label>
                    <Textarea
                      id="miracle-story"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={8}
                      placeholder="Please share your miracle story in as much detail as you feel comfortable. Include what happened, how you felt, and what the outcome was..."
                      className="border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                    />
                  </div>

                  <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                    <p className="text-sm text-gray-700">
                      <strong>Privacy:</strong> Your story will be kept confidential. If Dr. Miller wishes to include your story in future research or publications, she will contact you for permission first.
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white text-lg py-6"
                  >
                    <Send className="mr-2 w-5 h-5" />
                    {landingPageData.shareYourMiracle.ctaText}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-gradient-to-br from-amber-50 via-orange-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge className="mb-4 bg-amber-100 text-amber-800 hover:bg-amber-200">
                Get in Touch
              </Badge>
              <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
                {landingPageData.contact.title}
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                {landingPageData.contact.subtitle}
              </p>
            </div>

            <Card className="border-2 border-gray-200 shadow-xl bg-white">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        placeholder="John Doe"
                        className="border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        placeholder="john@example.com"
                        className="border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      Subject *
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      placeholder="Inquiry about..."
                      className="border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={6}
                      placeholder="Your message here..."
                      className="border-gray-300 focus:border-amber-500 focus:ring-amber-500"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white text-lg py-6"
                  >
                    Send Message
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                </form>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row justify-center items-center gap-6">
                    <a 
                      href={`mailto:${landingPageData.contact.email}`}
                      className="flex items-center space-x-2 text-gray-600 hover:text-amber-600 transition-colors"
                    >
                      <Mail className="w-5 h-5" />
                      <span>{landingPageData.contact.email}</span>
                    </a>
                    <a 
                      href={landingPageData.contact.blog}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-2 text-gray-600 hover:text-amber-600 transition-colors"
                    >
                      <BookOpen className="w-5 h-5" />
                      <span>Visit Blog</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8 mb-8">
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Book className="w-6 h-6 text-amber-400" />
                  <span className="text-xl font-bold">Dr. Carolyn Godschild Miller</span>
                </div>
                <p className="text-gray-400 leading-relaxed">
                  Author, Psychologist, and Spiritual Teacher exploring the intersection of science and spirituality.
                </p>
              </div>

              <div>
                <h4 className="font-semibold mb-4">Quick Links</h4>
                <ul className="space-y-2">
                  <li>
                    <button onClick={() => scrollToSection('books')} className="text-gray-400 hover:text-white transition-colors">
                      Books
                    </button>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-white transition-colors">
                      About
                    </button>
                  </li>
                  <li>
                    <a href={landingPageData.contact.blog} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">
                      Blog
                    </a>
                  </li>
                  <li>
                    <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors">
                      Contact
                    </button>
                  </li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-4">Books</h4>
                <ul className="space-y-2">
                  {landingPageData.books.map((book) => (
                    <li key={book.id}>
                      <a 
                        href={book.amazonLink} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        {book.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="border-t border-gray-800 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <p className="text-gray-400 text-sm">
                  © {new Date().getFullYear()} Dr. Carolyn Godschild Miller. All rights reserved.
                </p>
                <p className="text-gray-400 text-sm">
                  {landingPageData.contact.address}
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthorLandingPage;
